import React from "react";
import { useTranslation } from "react-i18next";
import { useCallOutcomeSubmit } from "./useCallOutcomeSubmit";
import NextCallModal from "../commonSections.jsx/NextCallModal";

/**
 * Mobile version of the call-outcome card. Same questions, same
 * validation, same submit behavior as desktop's NoteInfo.jsx - both
 * consume the exact same useCallOutcomeSubmit hook, so a rule change
 * there applies to both screens automatically. Only the visual style
 * (chip buttons instead of checkboxes, native date/time inputs instead
 * of the MUI picker) is mobile-specific.
 */
const MobileOutcomeCard = ({ contact, callType, onNextContact }) => {
  const { t } = useTranslation();

  const {
    channel,
    handleChannel,
    channels,
    nextCallModal,
    setNextCallModal,
    answered,
    setAnswered,
    confirmed,
    setConfirmed,
    confirmedRecovery,
    setConfirmedRecovery,
    scheduled,
    setScheduled,
    interested,
    setInterested,
    selectedDate,
    setSelectedDate,
    notes,
    setNotes,
    isSubmitting,
    error,
    noteError,
    setNoteError,
    channelError,
    isColdOrFollowUp,
    handleNextCall,
    submitAndGoNext,
  } = useCallOutcomeSubmit({ contact, callType, onNextContact });

  const dateValue = selectedDate ? selectedDate.format("YYYY-MM-DDTHH:mm") : "";

  const handleDateChange = (e) => {
    if (!e.target.value) return;
    setSelectedDate(selectedDate.clone().set({
      year: Number(e.target.value.slice(0, 4)),
      month: Number(e.target.value.slice(5, 7)) - 1,
      date: Number(e.target.value.slice(8, 10)),
      hour: Number(e.target.value.slice(11, 13)),
      minute: Number(e.target.value.slice(14, 16)),
    }));
  };

  const Chip = ({ active, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 text-[11px] font-bold rounded-lg py-2 px-2 border transition-colors ${
        active
          ? "bg-white text-icon border-white"
          : "bg-white/10 text-white border-white/30"
      }`}
    >
      {children}
    </button>
  );

  const questionTitle = () => {
    if (callType === "meeting-recovery") return t("agent.noteInfo.confirmedRecovery");
    if (callType === "meeting") return t("agent.noteInfo.confirmed");
    return t("agent.noteInfo.answered");
  };

  return (
    <div className="bg-icon rounded-2xl p-3.5 mx-4 mb-3 text-white">
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-xs font-extrabold m-0">{t("agent.nextCall.head", "Resultado de la llamada")}</p>
        <span className="bg-tertiary text-[9px] font-extrabold text-icon-dark px-2 py-0.5 rounded-full">
          {contact?.attempts || 0} {t("agent.noteInfo.attempt")}
        </span>
      </div>

      <p className="text-[11px] font-bold text-white/90 mb-1.5">{questionTitle()}?</p>

      {callType === "meeting-recovery" ? (
        <div className="flex gap-1.5 mb-2">
          <Chip active={confirmedRecovery === "yes"} onClick={() => setConfirmedRecovery("yes")}>
            {t("agent.noteInfo.yes")}
          </Chip>
          <Chip active={confirmedRecovery === "no"} onClick={() => setConfirmedRecovery("no")}>
            No
          </Chip>
        </div>
      ) : callType === "meeting" ? (
        <div className="flex gap-1.5 mb-2">
          <Chip active={confirmed === "yes"} onClick={() => setConfirmed("yes")}>
            {t("agent.noteInfo.yes")}
          </Chip>
          <Chip active={confirmed === "no"} onClick={() => setConfirmed("no")}>
            No
          </Chip>
          <Chip active={confirmed === "reschedule"} onClick={() => setConfirmed("reschedule")}>
            {t("agent.noteInfo.reschedule")}
          </Chip>
        </div>
      ) : (
        <div className="flex gap-1.5 mb-2">
          <Chip active={answered === "yes"} onClick={() => setAnswered("yes")}>
            {t("agent.noteInfo.yes")}
          </Chip>
          <Chip active={answered === "no"} onClick={() => setAnswered("no")}>
            No
          </Chip>
          <Chip active={answered === "callLater"} onClick={() => setAnswered("callLater")}>
            {t("agent.noteInfo.callLater")}
          </Chip>
        </div>
      )}

      {((callType === "meeting-recovery" && confirmedRecovery === "yes") ||
        (callType === "meeting" && confirmed === "reschedule") ||
        (isColdOrFollowUp && answered === "callLater")) && (
        <input
          type="datetime-local"
          value={dateValue}
          onChange={handleDateChange}
          className="w-full text-xs text-icon rounded-lg p-2 mb-2 border-none"
        />
      )}

      {answered === "yes" && isColdOrFollowUp && (
        <>
          <p className="text-[11px] font-bold text-white/90 mb-1.5 mt-2">
            {t("agent.noteInfo.interested")}?
          </p>
          <div className="flex gap-1.5 mb-2">
            <Chip active={interested === "yes"} onClick={() => setInterested("yes")}>
              {t("agent.noteInfo.yes")}
            </Chip>
            <Chip active={interested === "no"} onClick={() => setInterested("no")}>
              No
            </Chip>
            {callType === "follow-up" && (
              <Chip active={interested === "sendAgain"} onClick={() => setInterested("sendAgain")}>
                {t("agent.noteInfo.sendAgain")}
              </Chip>
            )}
          </div>

          {interested === "yes" && (
            <>
              <p className="text-[11px] font-bold text-white/90 mb-1.5">
                {t("agent.noteInfo.scheduled")}?
              </p>
              <div className="flex gap-1.5 mb-2">
                <Chip active={scheduled === "yes"} onClick={() => setScheduled("yes")}>
                  {t("agent.noteInfo.yes")}
                </Chip>
                <Chip active={scheduled === "no"} onClick={() => setScheduled("no")}>
                  No
                </Chip>
              </div>
              {scheduled === "yes" && (
                <input
                  type="datetime-local"
                  value={dateValue}
                  onChange={handleDateChange}
                  className="w-full text-xs text-icon rounded-lg p-2 mb-2 border-none"
                />
              )}
            </>
          )}

          {callType === "cold-calls" && (
            <select
              value={channel.channel}
              onChange={(e) => handleChannel("channel", e.target.value)}
              className="w-full text-xs text-icon rounded-lg p-2 mb-2 border-none"
            >
              <option value="">{t("agent.Channel")}</option>
              {channels.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          )}
        </>
      )}

      {!(isColdOrFollowUp && answered === "no") && (
        <>
          <p className="text-[10px] font-bold text-white/75 mb-1 mt-2">
            {t("agent.noteInfo.notes")}:
          </p>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              if (noteError && e.target.value.trim() !== "") setNoteError("");
            }}
            className="w-full text-xs text-icon rounded-lg p-2 mb-1.5 border-none resize-none"
          />
          {noteError && <p className="text-red-200 text-[10px] mb-1.5 m-0">{noteError}</p>}
        </>
      )}

      {error && <p className="text-red-200 text-[10px] mb-1.5 m-0">{error}</p>}
      {channelError && <p className="text-red-200 text-[10px] mb-1.5 m-0">{channelError}</p>}

      <button
        type="button"
        onClick={handleNextCall}
        disabled={isSubmitting}
        className="w-full bg-secondary text-white text-[13px] font-extrabold rounded-xl py-2.5 mt-1 disabled:opacity-70"
      >
        {isSubmitting ? t("button.loading") : t("agent.noteInfo.next")}
      </button>

      <NextCallModal
        open={nextCallModal}
        onClose={() => setNextCallModal(false)}
        onNextContact={submitAndGoNext}
      />
    </div>
  );
};

export default MobileOutcomeCard;